import EntityEditor from "../../../../../common/Tables/EntityEditor";
import { DayOfWeekSelector, DropdownSelector, ValidatedDateInput, ValidatedNumberInput, ValidatedTextInput } from "../../../../../common/inputs/ValidatedInputs";
import { ADJUSTMENT_SCOPES, ADJUSTMENT_TYPES, COMPARISON_OPERATORS, MODIFIER_TYPES } from "../../../../../common/enums";
import { Box, Collapse, MenuItem, Typography } from "@mui/material";
import { dateUtils } from "../../../../../utils/DateUtils";
import CheckboxList from "../../../../../common/inputs/CheckboxList";
import LabelledSwitch from "../../../../../common/inputs/LabelledSwitch";
import { usePriceModifiersData } from "../../../../../common/hooks/usePriceModifiersData";

const style = ({ theme }) => ({
    '& .MuiPaper-root': {
        maxWidth: '90%',
        width: 'max-content',
        minHeight: '500px'
    },
    '& .MuiDialogContent-root': {
        display: 'flex',
        gap: '50px'
    }
});

const border = {
    '&::before': {
        content: '""',
        position: 'absolute',
        left: '-25px',
        width: '1px',
        height: '100%',
        backgroundColor: 'primary.light',
    },
}

const emptyPriceModifier = {
    name: '',
    startDate: null,
    endDate: null,
    modifierType: MODIFIER_TYPES.ALL_THE_TIME,
    conditionValue: 0,
    comparisonOperator: COMPARISON_OPERATORS.EQUAL,
    adjustmentValue: 10,
    adjustmentType: ADJUSTMENT_TYPES.FIXED_AMOUNT,
    adjustmentScope: ADJUSTMENT_SCOPES.DAY,
    selectedPlanIds: [],
    selectedRoomIds: [],
};

const formatText = (text) => {
    const formatted = text.toLowerCase().replace(/_/g, ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const getConditionEditorComponent = (modifierType) => {
    switch (modifierType) {
        case MODIFIER_TYPES.DAY_OF_WEEK:
            return DayOfWeekConditionEditor;
        case MODIFIER_TYPES.LENGTH_OF_STAY:
        case MODIFIER_TYPES.NUMBER_OF_GUESTS:
            return LosOrNogConditionEditor;
        case MODIFIER_TYPES.ALL_THE_TIME:
        default:
            return null;
    }

}

const PriceModifiersEditor = ({
    onSuccess,
    onClose,
    editedPriceModifier,
    existingPriceModifiers,
    availableRooms,
    availablePlans,
    onError = () => {},
    propertyId,
}) => {
    const { createModifier, updateModifier } = usePriceModifiersData(propertyId);

    let defaultPriceModifier = editedPriceModifier || emptyPriceModifier;
    const isEditing = !!editedPriceModifier;

    const submitPriceModifier = (priceModifier) => {
        const submit = isEditing ? updateModifier : createModifier;

        return submit({
            ...priceModifier
        });
    }

    const title = isEditing ? `Editing Price Modifier: ${editedPriceModifier.name}` : "Creating a new Price Modifier";

    const EditorContent = ({ ...props }) => {
        return <PriceModifierCreatorContent
            {...props}
        />
    };

    return <EntityEditor
        dialogProps={{
            sx: style
        }}
        title={title}
        defaultEntity={defaultPriceModifier}
        entityName='Rate Plan'
        submitAction={submitPriceModifier}
        editing={isEditing}
        onClose={onClose}
        onSuccess={onSuccess}
        EditorContent={EditorContent}
        existingPriceModifiers={existingPriceModifiers}
        availableRooms={availableRooms}
        availablePlans={availablePlans}
        onError={onError}
    />
}

const PriceModifierCreatorContent = ({ entity, onChange, disabled, entityChanged, existingPriceModifiers, defaultEntity: defaultPriceModifier, editing: isEditing, availableRooms, availablePlans }) => {
    const priceModifier = entity;

    const onChangeModifierType = (id, value, valid) => {
        if (value === MODIFIER_TYPES.ALL_THE_TIME) {
            onChange([{
                id: id,
                value: value,
                valid: valid
            }, {
                id: 'conditionValue',
                value: 0,
                valid: true
            }, {
                id: 'comparisonOperator',
                value: COMPARISON_OPERATORS.EQUAL,
                valid: true
            }]);
        }
        else if (value === MODIFIER_TYPES.DAY_OF_WEEK) {
            onChange([{
                id: id,
                value: value,
                valid: valid
            }, {
                id: 'adjustmentScope',
                value: ADJUSTMENT_SCOPES.DAY,
                valid: true
            }, {
                id: 'conditionValue',
                value: 0,
                valid: true
            }, {
                id: 'comparisonOperator',
                value: COMPARISON_OPERATORS.EQUAL,
                valid: true
            }]);
        }
        else if (value === MODIFIER_TYPES.LENGTH_OF_STAY || value === MODIFIER_TYPES.NUMBER_OF_GUESTS) {
            onChange([{
                id: id,
                value: value,
                valid: valid
            }, {
                id: 'conditionValue',
                value: 1,
                valid: true
            }, {
                id: 'comparisonOperator',
                value: COMPARISON_OPERATORS.EQUAL,
                valid: true
            }]);
        }
    }

    return <>
        <Box sx={{
            display: 'flex', minWidth: '300px', flexDirection: 'column', position: 'relative',
        }}>
            <ValidatedTextInput
                margin="dense"
                id="name"
                label="Name"
                type="text"
                size='small'
                fullWidth
                required
                unique={{ existingValues: existingPriceModifiers.map(priceModifier => priceModifier.name).filter(name => !isEditing ? true : name !== defaultPriceModifier.name) }}
                onChange={onChange}
                disabled={disabled}
                value={priceModifier.name}
                disableError={!entityChanged}
            />
            <ValidatedDateInput
                id='startDate'
                label="Start date"
                value={priceModifier.startDate}
                onChange={onChange}
                disabled={disabled}
                textFieldProps={{
                    size: 'small',
                    sx: {
                        width: '220px'
                    }
                }}
                disableError={!entityChanged}
            />
            <ValidatedDateInput
                id='endDate'
                label="End date"
                value={priceModifier.endDate}
                onChange={onChange}
                disabled={disabled}
                textFieldProps={{
                    size: 'small',
                    sx: {
                        width: '220px'
                    }
                }}
                disableError={!entityChanged}
                minDate={priceModifier.startDate}
                defaultCalendarMonth={priceModifier.startDate}
            />
        </Box>
        <Box sx={{
            display: 'flex',
            minWidth: '300px',
            flexDirection: 'column',
            position: 'relative',
            gap: '20px',
            ...border,
        }}>
            <Box sx={{
                borderBottomColor: 'primary.light',
                borderBottomStyle: 'solid',
                borderBottomWidth: 1,
                paddingBottom: '8px'
            }}>
                <PriceAdjustmentEditor
                    priceModifier={priceModifier}
                    onChange={onChange}
                    disabled={disabled}
                />
                <LabelledSwitch
                    id='allTheTime'
                    label='All the time'
                    disabled={disabled}
                    checked={priceModifier.modifierType === MODIFIER_TYPES.ALL_THE_TIME}
                    onChange={(id, checked, valid) => onChangeModifierType('modifierType', checked ? MODIFIER_TYPES.ALL_THE_TIME : (defaultPriceModifier.modifierType !== MODIFIER_TYPES.ALL_THE_TIME ? defaultPriceModifier.modifierType : MODIFIER_TYPES.DAY_OF_WEEK), true)}
                />
            </Box>
            <ConditionEditor
                priceModifier={priceModifier}
                onChangeModifierType={onChangeModifierType}
                onChangeCondition={onChange}
                disabled={disabled}
                defaultPriceModifier={defaultPriceModifier}
            />
        </Box>
        <Box sx={{
            display: 'flex',
            minWidth: '300px',
            flexDirection: 'column',
            position: 'relative',
            gap: '30px',
            ...border,
        }}>
            <CheckboxList
                id='selectedRoomIds'
                label='Selected rooms'
                availableItems={availableRooms}
                selectedItems={priceModifier.selectedRoomIds.map(roomId => availableRooms.find(room => room.id === roomId))}
                disabled={disabled}
                onChange={(id, selectedItems, valid) => onChange(id, selectedItems.map(item => item.id), valid)}
            />
            <CheckboxList
                id='selectedPlanIds'
                label='Selected plans'
                availableItems={availablePlans}
                selectedItems={priceModifier.selectedPlanIds.map(planId => availablePlans.find(plan => plan.id === planId))}
                disabled={disabled}
                onChange={(id, selectedItems, valid) => onChange(id, selectedItems.map(item => item.id), valid)}
            />
        </Box>
    </>
}

const ModifierTypeSelector = ({ value, onChange, disabled }) => {
    return <>
        <DropdownSelector
            sx={{
                width: '200px'
            }}
            size='small'
            id="modifierType"
            onChange={(value) => onChange('modifierType', value, true)}
            disabled={disabled}
            value={value}
        >
            <MenuItem key={MODIFIER_TYPES.DAY_OF_WEEK} value={MODIFIER_TYPES.DAY_OF_WEEK}>{formatText(MODIFIER_TYPES.DAY_OF_WEEK)}</MenuItem>
            <MenuItem key={MODIFIER_TYPES.LENGTH_OF_STAY} value={MODIFIER_TYPES.LENGTH_OF_STAY}>{formatText(MODIFIER_TYPES.LENGTH_OF_STAY)}</MenuItem>
            <MenuItem key={MODIFIER_TYPES.NUMBER_OF_GUESTS} value={MODIFIER_TYPES.NUMBER_OF_GUESTS}>{formatText(MODIFIER_TYPES.NUMBER_OF_GUESTS)}</MenuItem>
        </DropdownSelector>
    </>
}

const ComparisonOperatorSelector = ({ value, onChange, disabled }) => {
    return <DropdownSelector
        sx={{ minWidth: 200 }}
        value={value}
        onChange={(value) => onChange('comparisonOperator', value, true)}
        disabled={disabled}
    >
        {Object.values(COMPARISON_OPERATORS).map(operator => <MenuItem key={operator} value={operator}>{formatText(operator)}</MenuItem>)}
    </DropdownSelector>
}

const DayOfWeekConditionEditor = ({ priceModifier, onChange, disabled }) => {
    return <DayOfWeekSelector
        id='conditionValue'
        value={dateUtils.getDaysOfWeekListFromFlag(priceModifier.conditionValue)}
        sx={{
            paddingTop: '10px',
            width: 'fit-content',
            marginBottom: '8px'
        }}
        onChange={(id, value, valid) => onChange(id, dateUtils.getFlagFromDaysOfWeekList(value), valid)}
        disabled={disabled}
    />
}

const LosOrNogConditionEditor = ({ priceModifier, onChange, disabled }) => {
    return <Box sx={{ display: 'flex', gap: '10px', alignItems: 'baseline' }}>
        <ComparisonOperatorSelector
            value={priceModifier.comparisonOperator}
            onChange={onChange}
            disabled={disabled}
        />
        <Typography sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>to</Typography>
        <ValidatedNumberInput
            id='conditionValue'
            size='small'
            value={priceModifier.conditionValue}
            onChange={onChange}
            disabled={disabled}
            sx={{ width: '80px' }}
            required
            min={1}
        />
    </Box>;
}

const IncreaseReduceSelector = ({ value, onChange, disabled }) => {
    return <DropdownSelector
        sx={{ minWidth: 110 }}
        value={value}
        onChange={onChange}
        disabled={disabled}
    >
        <MenuItem value={1}><Box sx={{ color: 'success.main', fontWeight: 500 }}>Increase</Box></MenuItem>
        <MenuItem value={-1}><Box sx={{ color: 'error.main', fontWeight: 500 }}>Reduce</Box></MenuItem>
    </DropdownSelector>
}

const AdjustmentScopeSelector = ({ value, onChange, disabled, readonly }) => {
    return <DropdownSelector
        value={value}
        onChange={onChange}
        disabled={disabled || readonly}
        className={readonly ? 'readonly' : ''}
        sx={{
            minWidth: 80,
            '&.readonly': {
                '& .MuiOutlinedInput-input': {
                    WebkitTextFillColor: (theme) => `${theme.palette.text.primary} !important`
                },
            }
        }}
    >
        <MenuItem value={ADJUSTMENT_SCOPES.DAY}>daily</MenuItem>
        <MenuItem value={ADJUSTMENT_SCOPES.TOTAL}>total</MenuItem>
    </DropdownSelector>
}

const PriceAdjustmentEditor = ({ priceModifier, onChange, disabled }) => {
    const onAdjustmentValueChange = (id, value, valid) => {
        const multiplyFactor = priceModifier.adjustmentValue < 0 ? -1 : 1;

        onChange(id, multiplyFactor * value, valid);
    }

    return <Box sx={{ display: 'flex', gap: '10px', alignItems: 'baseline' }}>
        <IncreaseReduceSelector
            value={priceModifier.adjustmentValue < 0 ? -1 : 1}
            onChange={(value) => onChange('adjustmentValue', value * Math.abs(priceModifier.adjustmentValue), true)}
            disabled={disabled}
        />
        <AdjustmentScopeSelector
            value={priceModifier.adjustmentScope}
            onChange={(value) => onChange('adjustmentScope', value, true)}
            disabled={disabled}
            readonly={priceModifier.modifierType === MODIFIER_TYPES.DAY_OF_WEEK}
        />
        <Typography sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>price by</Typography>
        <ValidatedNumberInput
            id='adjustmentValue'
            size='small'
            value={Math.abs(priceModifier.adjustmentValue)}
            onChange={onAdjustmentValueChange}
            disabled={disabled}
            hideButtons
            sx={{ width: '80px' }}
            required
            min={1}
            max={priceModifier.adjustmentValue < 0 && priceModifier.adjustmentType === ADJUSTMENT_TYPES.PERCENTAGE ? 100 : null}
        />
        <DropdownSelector
            sx={{ minWidth: 80 }}
            value={priceModifier.adjustmentType}
            onChange={(value) => onChange('adjustmentType', value, true)}
            disabled={disabled}
        >
            <MenuItem value={ADJUSTMENT_TYPES.FIXED_AMOUNT}>zł</MenuItem>
            <MenuItem value={ADJUSTMENT_TYPES.PERCENTAGE}>%</MenuItem>
        </DropdownSelector>
    </Box>
}

const ConditionEditor = ({ priceModifier, onChangeCondition, onChangeModifierType, disabled }) => {
    let ConditionEditorComponent = getConditionEditorComponent(priceModifier.modifierType);

    return <Collapse in={priceModifier.modifierType !== MODIFIER_TYPES.ALL_THE_TIME}>
        <Box>
            <Typography sx={{ fontWeight: 700 }}>When</Typography>
            <ModifierTypeSelector
                value={priceModifier.modifierType === MODIFIER_TYPES.ALL_THE_TIME ? '' : priceModifier.modifierType}
                onChange={onChangeModifierType}
                disabled={disabled}
            />
            <Typography sx={{ fontWeight: 700 }}>is</Typography>
            {ConditionEditorComponent && <ConditionEditorComponent
                priceModifier={priceModifier}
                onChange={onChangeCondition}
                disabled={disabled}
            />}
        </Box>
    </Collapse>
}

export default PriceModifiersEditor;